import AddIcon from 'mdi-react/AddIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import React from 'react';
import { Link } from "react-router-dom";
import { toCurrency, ifV2elseV3AccountHolderId } from '@/shared/helpers'

const footerRenderer = (info, field) => {
  const { rows } = info;
  const totalValue = rows.reduce((sum, row) => row.original.dinamicDepth === 1 ? Number(row.values[field]) + sum : 0 + sum, 0);
  return <span>{toCurrency(totalValue)}</span>;
}

const totalSubRowsRenderer = (row, value, label) => {
  let totalSubRows = 0;
  const cb = (e) => {
    totalSubRows = Number(e.values[label]) + totalSubRows;
    e.subRows && e.subRows.forEach(cb);
  }
  row.subRows.forEach(cb);

  const result = row.subRows.length === 0 || row.isExpanded ? toCurrency(value) : toCurrency(totalSubRows);
  return <div>{result}</div>
}

const expandAllSubprogramsRow = (rows, toggleRowExpanded) => {
  rows.forEach((row) => {
    if (row.id) {
      toggleRowExpanded(row.id, true);
    }
    if (row.subRows && row.subRows.length > 0) {
      expandAllSubprogramsRow(row.subRows, toggleRowExpanded);
    }
  });
};

export const JOURNAL_DETAILED_COLUMNS = [
  {
    Header: 'General Info',
    columns: [
      {
        // Build our expander column
        id: 'expander', // Make sure it has an ID
        Header: '',
        Cell: ({ row, toggleRowExpanded }) =>
          // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
          // to build the toggle for expanding a row
          row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: {
                  // We can even use the row.depth property
                  // and paddingLeft to indicate the depth
                  // of the row
                  paddingLeft: `${row.depth * 2}rem`,
                },
                onClick: (e) => {
                  e.stopPropagation();
                  row.toggleRowExpanded();
                  if (row.subRows) {
                    row.subRows.forEach((subRow) => {
                      if (subRow.id) {
                        expandAllSubprogramsRow(row.subRows, toggleRowExpanded);
                      }
                    });
                  }
                },
              })}
            >
              {row.isExpanded ? <MinusIcon className="chevron-expand" /> : <AddIcon className="chevron-expand" />}
            </span>
          ) : null,
        maxWidth: 80,
        noAlignRight: true
      },
      {
        Header: "Program Name",
        disableSortBy: true,
        accessor: "name",
        Footer: "Page Total",
        Cell: ({ row, value }) => {
          const space = row.original.dinamicDepth === 1 ? '' : ' • ';
          const boldRow = row.original.dinamicDepth === 1;
          return <div style={boldRow ? { fontWeight: 'bold' } : {}}>{space.repeat(row.original.dinamicDepth)}
            {row.original.id ? <Link
              to={`/program/view/${row.original.id}`}>{
                row.isExpanded && row.depth === 0 ? value + ' Total' : value
              }</Link> : value ? <span style={{ fontWeight: 'bold' }}>{value + ' Total'}</span> : ""}
          </div>
        },
        minWidth: 200,
        className: 'frozenColumn',
      },
      {
        Header: "Program ID",
        disableSortBy: true,
        accessor: "shownId",
        Cell: ({ row }) => {
          return ifV2elseV3AccountHolderId(row.original)
        },
        maxWidth: 100,
      },
      {
        Header: "Deposits",
        disableSortBy: true,
        accessor: "deposits",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'deposits') : "",
        Footer: (info) => footerRenderer(info, 'deposits'),
        maxWidth: 100,
      },
      {
        Header: "Deposit Reversal",
        disableSortBy: true,
        accessor: "deposit_reversal",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'deposit_reversal') : "",
        Footer: (info) => footerRenderer(info, 'deposit_reversal'),
        maxWidth: 130,
      },
      {
        Header: "Points Purchased",
        disableSortBy: true,
        accessor: "points_purchased",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'points_purchased') : "",
        Footer: (info) => footerRenderer(info, 'points_purchased'),
        maxWidth: 100,
      },
      {
        Header: "Points Reclaimed",
        disableSortBy: true,
        accessor: "reclaims",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'reclaims') : "",
        Footer: (info) => footerRenderer(info, 'reclaims'),
        maxWidth: 100,
      },
      {
        Header: "Award Credit Reclaimed",
        disableSortBy: true,
        accessor: "award_credit_reclaims",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'award_credit_reclaims') : "",
        Footer: (info) => footerRenderer(info, 'award_credit_reclaims'),
        maxWidth: 100,
      },
      {
        Header: "Net Points purchased",
        disableSortBy: true,
        accessor: "net_points_purchased",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'net_points_purchased') : "",
        Footer: (info) => footerRenderer(info, 'net_points_purchased'),
        maxWidth: 100,
      },
    ],
    Footer: 'General Info',
  },
  {
    Header: "Fees",
    columns: [
      {
        Header: "Deposit Fee",
        disableSortBy: true,
        accessor: "deposit_fee",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'deposit_fee') : "",
        Footer: (info) => footerRenderer(info, 'deposit_fee'),
        maxWidth: 100,
      },
      {
        Header: "Deposit Fee Reversal",
        disableSortBy: true,
        accessor: "deposit_fee_reversal",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'deposit_fee_reversal') : "",
        Footer: (info) => footerRenderer(info, 'deposit_fee_reversal'),
        maxWidth: 100,
      },
      {
        Header: "Convenience Fees",
        disableSortBy: true,
        accessor: "convenience_fees",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'convenience_fees') : "",
        Footer: (info) => footerRenderer(info, 'convenience_fees'),
        maxWidth: 100,
      },
      {
        Header: "License Fee",
        disableSortBy: true,
        accessor: "fixed_fee",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'fixed_fee') : "",
        Footer: (info) => footerRenderer(info, 'fixed_fee'),
        maxWidth: 100,
      },
      {
        Header: "Usage Fee",
        disableSortBy: true,
        accessor: "usage_fee",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'usage_fee') : "",
        Footer: (info) => footerRenderer(info, 'usage_fee'),
        maxWidth: 100,
      },
      {
        Header: "Setup Fee",
        disableSortBy: true,
        accessor: "setup_fee",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'setup_fee') : "",
        Footer: (info) => footerRenderer(info, 'setup_fee'),
        maxWidth: 100,
      },
      {
        Header: "Premium Fee Billed to Client",
        disableSortBy: true,
        accessor: "premium_fee",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'premium_fee') : "",
        Footer: (info) => footerRenderer(info, 'premium_fee'),
        maxWidth: 140,
      }
    ],
    Footer: "Fees"
  },
  {
    Header: 'Others',
    columns: [
      {
        Header: "Points Redeemed",
        disableSortBy: true,
        accessor: "points_redeemed",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'points_redeemed') : "",
        Footer: (info) => footerRenderer(info, 'points_redeemed'),
        maxWidth: 100,
      },
      {
        Header: "Premium From Codes Redeemed",
        disableSortBy: true,
        accessor: "codes_redeemed_premium",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'codes_redeemed_premium') : "",
        Footer: (info) => footerRenderer(info, 'codes_redeemed_premium'),
        maxWidth: 120,
      },
      {
        Header: "Cost of Codes Redeemed",
        disableSortBy: true,
        accessor: "codes_redeemed_cost",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'codes_redeemed_cost') : "",
        Footer: (info) => footerRenderer(info, 'codes_redeemed_cost'),
        maxWidth: 100,
      },
      {
        Header: "Program Funds net transfers",
        disableSortBy: true,
        accessor: "program_funds_net_transfers",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'program_funds_net_transfers') : "",
        Footer: (info) => footerRenderer(info, 'program_funds_net_transfers'),
        maxWidth: 140,
      },
      {
        Header: "Program refunds for monies pending",
        disableSortBy: true,
        accessor: "program_refunds_for_monies_pending",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'program_refunds_for_monies_pending') : "",
        Footer: (info) => footerRenderer(info, 'program_refunds_for_monies_pending'),
        maxWidth: 140,
      },
      {
        Header: "Total Spend Rebate",
        disableSortBy: true,
        accessor: "total_spend_rebate",
        Cell: ({ row, value }) => value ? totalSubRowsRenderer(row, value, 'total_spend_rebate') : "",
        Footer: (info) => footerRenderer(info, 'total_spend_rebate'),
        maxWidth: 100,
      }
    ],
    Footer: 'Others'
  }
]
